import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.REACT_APP_WORDPRESS_URL;
const CONSUMER_KEY = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY;
const CONSUMER_SECRET = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET;

// Create axios instance for WooCommerce API
const wooAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/wc/v3`,
  auth: {
    username: CONSUMER_KEY,
    password: CONSUMER_SECRET,
  },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for custom API endpoints
const customAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/headless-checkout/v1`,
  auth: {
    username: CONSUMER_KEY,
    password: CONSUMER_SECRET,
  },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token for authenticated requests
const addAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

addAuthInterceptor(wooAPI);
addAuthInterceptor(customAPI);

export const orderService = {
  // Get available payment gateways from WordPress
  async getPaymentGateways() {
    try {
      const response = await wooAPI.get('/payment_gateways');
      const enabledGateways = response.data.filter(gateway => gateway.enabled === true);
      return enabledGateways;
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      return [
        {
          id: 'bacs',
          title: 'Direct Bank Transfer',
          description: 'Make your payment directly into our bank account.',
          enabled: true,
        },
        {
          id: 'cod',
          title: 'Cash on Delivery',
          description: 'Pay with cash upon delivery.',
          enabled: true,
        }
      ];
    }
  },

  async createOrder(orderData, userEmail = null, userData = {}) {
    try {
      if (userEmail && !orderData.customer_id) {
        const customer = await this.getOrCreateCustomer(userEmail, userData);
        if (customer) {
          orderData.customer_id = customer.id;
        }
      }

      const response = await wooAPI.post('/orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create order';
      throw new Error(errorMessage);
    }
  },

  async createOrderWithRazorpay(orderData, userEmail = null, userData = {}) {
    try {
      const order = await this.createOrder(orderData, userEmail, userData);
      const razorpayDetails = await this.getRazorpayOrderDetails(order.id);
      
      return {
        order,
        razorpayDetails
      };
    } catch (error) {
      console.error('Error creating order with Razorpay:', error);
      throw error;
    }
  },

  async getRazorpayOrderDetails(orderId) {
    try {
      const response = await customAPI.get(`/razorpay-order/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Razorpay order details:', error);
      const errorMessage = error.response?.data?.message || 'Failed to get payment details';
      throw new Error(errorMessage);
    }
  },

  async verifyRazorpayPayment(paymentData) {
    try {
      const response = await customAPI.post('/verify-payment', paymentData);
      return response.data;
    } catch (error) {
      console.error('Error verifying Razorpay payment:', error);
      const errorMessage = error.response?.data?.message || 'Payment verification failed';
      throw new Error(errorMessage);
    }
  },

  async waitForPaymentCompletion(orderId, maxAttempts = 10, intervalMs = 2000) {
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          attempts++;
          const order = await this.getOrderById(orderId);
          
          if (['completed', 'processing'].includes(order.status)) {
            resolve(order);
            return;
          }
          
          if (['failed', 'cancelled', 'refunded'].includes(order.status)) {
            reject(new Error(`Payment failed. Order status: ${order.status}`));
            return;
          }
          
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, intervalMs);
          } else {
            reject(new Error('Payment verification timeout'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      checkStatus();
    });
  },

  async getOrders(customerId = null, params = {}) {
    try {
      const queryParams = {
        per_page: params.per_page || 10,
        page: params.page || 1,
        orderby: 'date',
        order: 'desc',
        ...params,
      };

      if (customerId) {
        queryParams.customer = customerId;
      }

      const response = await wooAPI.get('/orders', { params: queryParams });
      return {
        orders: response.data,
        totalPages: parseInt(response.headers['x-wp-totalpages']) || 1,
        total: parseInt(response.headers['x-wp-total']) || 0,
      };
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw new Error('Failed to fetch orders');
    }
  },

  async getOrderById(orderId) {
    try {
      const response = await wooAPI.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw new Error('Order not found');
    }
  },

  // Updated method to get customer downloads using the correct WooCommerce API
  async getCustomerDownloads(customerId) {
    try {
      const response = await wooAPI.get(`/customers/${customerId}/downloads`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer downloads:', error);
      return [];
    }
  },

  // Improved method to get all customer downloads
  async getAllCustomerDownloads(customerId) {
    try {
      // Method 1: Try the direct customer downloads endpoint
      try {
        const directDownloads = await wooAPI.get(`/customers/${customerId}/downloads`);
        if (directDownloads.data && directDownloads.data.length > 0) {
          return this.formatDownloads(directDownloads.data);
        }
      } catch (error) {
        console.warn('Direct downloads endpoint failed:', error);
      }

      // Method 2: Get downloads from completed orders
      const ordersResponse = await this.getOrders(customerId, { 
        per_page: 100,
        status: 'completed'
      });
      
      const allDownloads = [];
      
      for (const order of ordersResponse.orders) {
        const orderDownloads = await this.getOrderDownloads(order.id, customerId);
        allDownloads.push(...orderDownloads);
      }

      return allDownloads;
    } catch (error) {
      console.error('Error fetching all customer downloads:', error);
      return [];
    }
  },

  // Enhanced method to get downloads for a specific order
  async getOrderDownloads(orderId, customerId = null) {
    try {
      const order = await this.getOrderById(orderId);
      const downloads = [];

      // Only process completed orders
      if (order.status !== 'completed') {
        return downloads;
      }

      for (const item of order.line_items) {
        try {
          // Get product details
          const productResponse = await wooAPI.get(`/products/${item.product_id}`);
          const product = productResponse.data;
          
          // Check if product is downloadable
          if (product.downloadable && product.downloads && product.downloads.length > 0) {
            for (const download of product.downloads) {
              // Create download permission entry
              const downloadItem = {
                id: `${order.id}-${item.product_id}-${download.id}`,
                name: download.name || download.file.split('/').pop(),
                file: download.file,
                product_name: item.name,
                product_id: item.product_id,
                order_id: orderId,
                download_id: download.id,
                downloads_remaining: item.quantity, // Based on purchased quantity
                access_expires: this.calculateDownloadExpiry(order.date_created, product),
                download_url: this.getSecureDownloadUrl(download.file, orderId, item.product_id, download.id),
                order_date: order.date_created,
                order_status: order.status,
              };
              
              downloads.push(downloadItem);
            }
          }
        } catch (productError) {
          console.warn(`Error fetching product ${item.product_id}:`, productError);
        }
      }

      return downloads;
    } catch (error) {
      console.error('Error fetching order downloads:', error);
      return [];
    }
  },

  // Helper method to format downloads from WooCommerce API
  formatDownloads(downloads) {
    return downloads.map(download => ({
      id: download.download_id || download.id,
      name: download.download_name || download.name,
      file: download.download_url || download.file,
      product_name: download.product_name,
      product_id: download.product_id,
      order_id: download.order_id,
      downloads_remaining: download.downloads_remaining,
      access_expires: download.access_expires,
      download_url: download.download_url || this.getSecureDownloadUrl(
        download.file, 
        download.order_id, 
        download.product_id, 
        download.download_id
      ),
    }));
  },

  // Calculate download expiry based on product settings
  calculateDownloadExpiry(orderDate, product) {
    // Default to no expiry if not set
    if (!product.download_expiry || product.download_expiry <= 0) {
      return null;
    }

    const orderDateObj = new Date(orderDate);
    const expiryDate = new Date(orderDateObj);
    expiryDate.setDate(expiryDate.getDate() + product.download_expiry);
    
    return expiryDate.toISOString();
  },

  // Check if order has downloadable products
  async hasDownloadableProducts(orderId) {
    try {
      const order = await this.getOrderById(orderId);
      
      for (const item of order.line_items) {
        try {
          const productResponse = await wooAPI.get(`/products/${item.product_id}`);
          const product = productResponse.data;
          
          if (product.downloadable) {
            return true;
          }
        } catch (productError) {
          console.warn(`Error checking product ${item.product_id}:`, productError);
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking downloadable products:', error);
      return false;
    }
  },

  async updateOrderStatus(orderId, status, note = '') {
    try {
      const updateData = { status };
      if (note) {
        updateData.customer_note = note;
      }

      const response = await wooAPI.put(`/orders/${orderId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error('Failed to update order');
    }
  },

  async cancelOrder(orderId, reason = '') {
    try {
      const response = await wooAPI.put(`/orders/${orderId}`, {
        status: 'cancelled',
        customer_note: reason,
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw new Error('Failed to cancel order');
    }
  },

  async getOrderNotes(orderId) {
    try {
      const response = await wooAPI.get(`/orders/${orderId}/notes`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order notes:', error);
      throw new Error('Failed to fetch order notes');
    }
  },

  async addOrderNote(orderId, note, customerNote = false) {
    try {
      const response = await wooAPI.post(`/orders/${orderId}/notes`, {
        note,
        customer_note: customerNote,
      });
      return response.data;
    } catch (error) {
      console.error('Error adding order note:', error);
      throw new Error('Failed to add order note');
    }
  },

async getOrCreateCustomer(userEmail, userData = {}) {
    try {
      const existingCustomersResponse = await wooAPI.get('/customers', {
        params: { email: userEmail }
      });

      if (existingCustomersResponse.data.length > 0) {
        return existingCustomersResponse.data[0];
      }

      // Handle single name field
      const nameParts = userData.name ? userData.name.trim().split(' ') : [''];
      const firstName = userData.firstName || nameParts[0] || '';
      const lastName = userData.lastName || nameParts.slice(1).join(' ') || '';

      const customerData = {
        email: userEmail,
        first_name: firstName,
        last_name: lastName,
        billing: userData.billingDetails || {},
        shipping: userData.shippingDetails || {},
      };

      const newCustomerResponse = await wooAPI.post('/customers', customerData);
      return newCustomerResponse.data;
    } catch (error) {
      console.error('Error getting/creating customer:', error);
      return null;
    }
  },

  isVirtualCart(cartItems) {
    return cartItems.every(item => item.virtual === true || item.downloadable === true);
  },

  prepareOrderData(cartItems, billingDetails, shippingDetails = null, paymentMethod = 'razorpay', customerId = null) {
    const lineItems = cartItems.map(item => ({
      product_id: item.id,
      quantity: item.quantity,
      price: item.price,
    }));

    const isVirtualOnly = this.isVirtualCart(cartItems);

    // Split the full name into first and last name
    const nameParts = billingDetails.name ? billingDetails.name.trim().split(' ') : [''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const orderData = {
      payment_method: paymentMethod,
      payment_method_title: this.getPaymentMethodTitle(paymentMethod),
      set_paid: false,
      status: 'pending',
      billing: {
        first_name: firstName,
        last_name: lastName,
        email: billingDetails.email,
        phone: billingDetails.phone || '',
      },
      line_items: lineItems,
    };

    if (!isVirtualOnly) {
      orderData.billing = {
        ...orderData.billing,
        address_1: billingDetails.address1 || '',
        address_2: billingDetails.address2 || '',
        city: billingDetails.city || '',
        state: billingDetails.state || '',
        postcode: billingDetails.postcode || '',
        country: billingDetails.country || 'IN',
      };

      orderData.shipping = shippingDetails ? {
        first_name: shippingDetails.name ? shippingDetails.name.split(' ')[0] : firstName,
        last_name: shippingDetails.name ? shippingDetails.name.split(' ').slice(1).join(' ') : lastName,
        address_1: shippingDetails.address1,
        address_2: shippingDetails.address2 || '',
        city: shippingDetails.city,
        state: shippingDetails.state,
        postcode: shippingDetails.postcode,
        country: shippingDetails.country,
      } : {
        first_name: firstName,
        last_name: lastName,
        address_1: billingDetails.address1 || '',
        address_2: billingDetails.address2 || '',
        city: billingDetails.city || '',
        state: billingDetails.state || '',
        postcode: billingDetails.postcode || '',
        country: billingDetails.country || 'IN',
      };

      orderData.shipping_lines = [
        {
          method_id: 'flat_rate',
          method_title: 'Flat Rate',
          total: '0.00',
        },
      ];
    }

    if (customerId) {
      orderData.customer_id = customerId;
    }

    return orderData;
  },

  getPaymentMethodTitle(method) {
    const methods = {
      bacs: 'Direct Bank Transfer',
      cheque: 'Check Payments',
      cod: 'Cash on Delivery',
      paypal: 'PayPal',
      stripe: 'Credit Card (Stripe)',
      razorpay: 'Razorpay',
      square: 'Square',
      free: 'Free Order',
    };
    return methods[method] || method;
  },

  formatOrderStatus(status) {
    const statuses = {
      pending: 'Pending Payment',
      processing: 'Processing',
      'on-hold': 'On Hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
      failed: 'Failed',
    };
    return statuses[status] || status;
  },

  calculateOrderTotal(lineItems) {
    return lineItems.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0);
  },

  formatOrderDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },

  formatDownloadFileName(fileName) {
    return fileName.split('/').pop() || fileName;
  },

   isDownloadExpired(expiryDate) {
    // Handle 'never' expiry
    if (!expiryDate || expiryDate === 'never' || expiryDate === 'unlimited') {
      return false;
    }
    
    // Handle actual date expiry
    try {
      return new Date(expiryDate) < new Date();
    } catch (error) {
      console.warn('Invalid expiry date format:', expiryDate);
      return false; // Default to not expired if date is invalid
    }
  },

  // Updated: Format download file name
  formatDownloadFileName(fileName) {
    if (!fileName) return 'download';
    
    // If it's a URL, extract the filename
    if (fileName.includes('/')) {
      return fileName.split('/').pop() || 'download';
    }
    
    return fileName;
  },

  // Updated: Get download URL with proper authentication
  getSecureDownloadUrl(downloadFile, orderId, productId, downloadId = null) {
    // If it's already a full URL, return as is
    if (downloadFile && downloadFile.startsWith('http')) {
      return downloadFile;
    }
    
    // Construct secure download URL through WooCommerce
    const params = new URLSearchParams({
      action: 'woocommerce_download_product',
      order: orderId,
      product: productId,
    });

    if (downloadId) {
      params.append('download_id', downloadId);
    }

    if (downloadFile) {
      params.append('download_file', downloadFile);
    }

    return `${API_BASE_URL}/wp-admin/admin-ajax.php?${params.toString()}`;
  },

  // New: Helper to determine if download has unlimited access
  hasUnlimitedDownloads(download) {
    return download.downloads_remaining === 'unlimited' || 
           download.downloads_remaining === 'never' ||
           download.downloads_remaining === -1;
  },

  // New: Helper to get numeric downloads remaining
  getNumericDownloadsRemaining(download) {
    if (this.hasUnlimitedDownloads(download)) {
      return Infinity;
    }
    
    const remaining = parseInt(download.downloads_remaining);
    return isNaN(remaining) ? 0 : remaining;
  },

  // Enhanced secure download URL generation
  getSecureDownloadUrl(downloadFile, orderId, productId, downloadId = null) {
    // If it's already a full URL, return as is
    if (downloadFile && downloadFile.startsWith('http')) {
      return downloadFile;
    }
    
    // Construct secure download URL through WooCommerce
    const params = new URLSearchParams({
      action: 'woocommerce_download_product',
      order: orderId,
      product: productId,
    });

    if (downloadId) {
      params.append('download_id', downloadId);
    }

    if (downloadFile) {
      params.append('download_file', downloadFile);
    }

    return `${API_BASE_URL}/wp-admin/admin-ajax.php?${params.toString()}`;
  },
};